import React from 'react';
import { Filter } from 'lucide-react';
import ReactSelect from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import { DOCUMENT_CATEGORIES_OPTIONS } from '@pages/projects/constants';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { FormGroup } from '@components/molecules/form/FormGroup';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { SelectOption } from '@components/atoms/ReactSelect';

interface FilterBarProps {
  filterText: string;
  filterCategory: string;
  onFilterTextChange: (value: string) => void;
  onFilterCategoryChange: (value: string) => void;
  onClearFilters: () => void;
  projects: any[];
  selectedProjectId: string | null;
  onProjectChange: (projectId: string | null) => void;
  selectedDocuments: any[];
  onDocumentsChange: (documents: any[]) => void;
  documents: any[];
  showActions?: boolean;
  projectId?: string | null;
  handleOpenManageDocuments?: () => void;
  isReadOnly?: boolean;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  projects,
  documents,
  filterText,
  filterCategory,
  onClearFilters,
  onProjectChange,
  selectedProjectId,
  selectedDocuments,
  onDocumentsChange,
  onFilterTextChange,
  onFilterCategoryChange,
  showActions,
  projectId,
  handleOpenManageDocuments,
  isReadOnly = false,
}) => {
  const hasActiveFilters =
    filterText ||
    filterCategory ||
    selectedProjectId ||
    selectedDocuments.length > 0;

  // Convert projects to SelectOption format
  const projectOptions: SelectOption[] = projects.map((project) => ({
    value: project.projectId,
    label: project.name,
  }));

  // Get unique tasks and convert to SelectOption format
  const uniqueTasks = Array.from(
    new Map(
      documents
        .filter(
          (doc) =>
            !selectedProjectId || doc.task?.projectId === selectedProjectId,
        )
        .map((doc) => [doc.taskId, doc.task]),
    ).values(),
  ).filter((task) => task); // Filter out undefined tasks

  const taskOptions: SelectOption[] = uniqueTasks.map((task) => ({
    value: task.taskId,
    label: task.name || 'Unknown Task',
  }));

  const handleProjectChange = (
    value: SingleValue<SelectOption> | MultiValue<SelectOption>,
  ) => {
    const singleValue = value as SingleValue<SelectOption>;
    onProjectChange((singleValue?.value as string) || null);
  };

  const handleTaskChange = (
    value: SingleValue<SelectOption> | MultiValue<SelectOption>,
  ) => {
    const singleValue = value as SingleValue<SelectOption>;
    if (singleValue?.value) {
      const task = documents.find((doc) => doc.taskId === singleValue.value);
      onDocumentsChange(task ? [task] : []);
    } else {
      onDocumentsChange([]);
    }
  };

  const handleCategoryChange = (
    value: SingleValue<SelectOption> | MultiValue<SelectOption>,
  ) => {
    const singleValue = value as SingleValue<SelectOption>;
    onFilterCategoryChange((singleValue?.value as string) || '');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Filters
          </h3>
        </div>
        <div className="flex items-center space-x-2">
          {showActions && handleOpenManageDocuments && !isReadOnly && (
            <Button
              onClick={handleOpenManageDocuments}
              variant="secondary"
              className="flex items-center space-x-2">
              <PostAddIcon sx={{ fontSize: 16 }} />
              <span>Manage Documents</span>
            </Button>
          )}
          {hasActiveFilters && (
            <Button
              onClick={onClearFilters}
              variant="tertiary"
              className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-150">
              Clear all
            </Button>
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="block md:hidden space-y-4">
        {/* Project Filter - Full Width on Mobile */}
        {!projectId && (
          <div className="w-full">
            <FormGroup label="Project" htmlFor="projectSelect">
              <ReactSelect
                isClearable={true}
                isSearchable={true}
                options={projectOptions}
                placeholder="All Projects"
                onChange={handleProjectChange}
                value={
                  selectedProjectId
                    ? projectOptions.find((p) => p.value === selectedProjectId)
                    : null
                }
              />
            </FormGroup>
          </div>
        )}

        {/* Name Filter - Full Width on Mobile */}
        <div className="w-full">
          <FormGroup label="Document Name" htmlFor="documentName">
            <Input
              type="text"
              value={filterText}
              placeholder="Search documents..."
              onChange={(e) => onFilterTextChange(e.target.value)}
            />
          </FormGroup>
        </div>

        {/* Task and Category in 2 columns on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="Task" htmlFor="taskSelect">
            <ReactSelect
              isClearable={true}
              isSearchable={true}
              options={taskOptions}
              placeholder="All Tasks"
              onChange={handleTaskChange}
              isDisabled={!selectedProjectId && projects.length > 0}
              value={
                selectedDocuments.length > 0
                  ? taskOptions.find(
                      (t) => t.value === selectedDocuments[0]?.taskId,
                    )
                  : null
              }
            />
          </FormGroup>

          <FormGroup label="Category" htmlFor="categorySelect">
            <ReactSelect
              placeholder="All Categories"
              options={DOCUMENT_CATEGORIES_OPTIONS}
              value={
                filterCategory
                  ? DOCUMENT_CATEGORIES_OPTIONS.find(
                      (c) => c.value === filterCategory,
                    )
                  : null
              }
              onChange={handleCategoryChange}
              isClearable={true}
              isSearchable={true}
            />
          </FormGroup>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex justify-between flex-wrap gap-4">
        {/* Project Filter */}
        {!projectId && (
          <div className="min-w-0 flex-1 max-w-xs">
            <FormGroup label="Project" htmlFor="projectSelectDesktop">
              <ReactSelect
                isClearable={true}
                isSearchable={true}
                options={projectOptions}
                placeholder="All Projects"
                onChange={handleProjectChange}
                value={
                  selectedProjectId
                    ? projectOptions.find((p) => p.value === selectedProjectId)
                    : null
                }
              />
            </FormGroup>
          </div>
        )}

        {/* Name Filter */}
        <div className="min-w-0 flex-1 max-w-sm">
          <FormGroup label="Document Name" htmlFor="documentNameDesktop">
            <Input
              type="text"
              value={filterText}
              placeholder="Search documents..."
              onChange={(e) => onFilterTextChange(e.target.value)}
            />
          </FormGroup>
        </div>

        {/* Task Filter */}
        <div className="min-w-0 flex-1 max-w-xs">
          <FormGroup label="Task" htmlFor="taskSelectDesktop">
            <ReactSelect
              isClearable={true}
              isSearchable={true}
              options={taskOptions}
              placeholder="All Tasks"
              onChange={handleTaskChange}
              isDisabled={!selectedProjectId && projects.length > 0}
              value={
                selectedDocuments.length > 0
                  ? taskOptions.find(
                      (t) => t.value === selectedDocuments[0]?.taskId,
                    )
                  : null
              }
            />
          </FormGroup>
        </div>

        {/* Category Filter */}
        <div className="min-w-0 flex-1 max-w-xs">
          <FormGroup label="Category" htmlFor="categorySelectDesktop">
            <ReactSelect
              placeholder="All Categories"
              options={DOCUMENT_CATEGORIES_OPTIONS}
              value={
                filterCategory
                  ? DOCUMENT_CATEGORIES_OPTIONS.find(
                      (c) => c.value === filterCategory,
                    )
                  : null
              }
              onChange={handleCategoryChange}
              isClearable={true}
              isSearchable={true}
            />
          </FormGroup>
        </div>
      </div>
    </div>
  );
};
